import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    LogOut,
    Phone,
    Mail,
    Calendar as CalendarIcon,
    MapPin,
    Car,
    Wrench,
    RefreshCw,
    X,
    Trash2,
    ChevronRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { BRAND } from "@/lib/brand";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FLOW = ["new", "contacted", "scheduled", "completed", "cancelled"];
const STATUS_LABEL = {
    new: "New",
    contacted: "Contacted",
    scheduled: "Scheduled",
    completed: "Completed",
    cancelled: "Cancelled",
};
const STATUS_COLOR = {
    new: "bg-[#1d4ed8] text-white",
    contacted: "bg-[#0a0a0a] text-white",
    scheduled: "bg-[#facc15] text-[#0a0a0a]",
    completed: "bg-[#16a34a] text-white",
    cancelled: "bg-[#52525b] text-white",
};

function StatusBadge({ status }) {
    const cls = STATUS_COLOR[status] || "bg-[#0a0a0a] text-white";
    return (
        <span
            data-testid={`status-badge-${status}`}
            className={`inline-block font-mono-tech text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 ${cls}`}
        >
            {STATUS_LABEL[status] || status}
        </span>
    );
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return String(iso);
    }
}

export default function AdminDashboard() {
    const { user, logout, authHeaders } = useAuth();
    const [tab, setTab] = useState("bookings"); // "bookings" | "messages"
    const [bookings, setBookings] = useState([]);
    const [messages, setMessages] = useState([]);
    const [stats, setStats] = useState({ total: 0, by_status: {} });
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = filter === "all" ? {} : { status_filter: filter };
            const [bRes, sRes, mRes] = await Promise.all([
                axios.get(`${API}/admin/bookings`, {
                    headers: authHeaders,
                    params,
                }),
                axios.get(`${API}/admin/bookings/stats`, {
                    headers: authHeaders,
                }),
                axios.get(`${API}/admin/contact`, {
                    headers: authHeaders,
                }),
            ]);
            setBookings(bRes.data);
            setStats(sRes.data);
            setMessages(mRes.data);
        } catch (err) {
            toast.error("Could not load dashboard");
        } finally {
            setLoading(false);
        }
    }, [authHeaders, filter]);

    useEffect(() => {
        load();
    }, [load]);

    const updateStatus = useCallback(
        async (id, status) => {
            try {
                const { data } = await axios.patch(
                    `${API}/admin/bookings/${id}/status`,
                    { status },
                    { headers: authHeaders }
                );
                setBookings((prev) =>
                    prev.map((b) => (b.id === id ? { ...b, ...data } : b))
                );
                setSelected((cur) =>
                    cur && cur.id === id ? { ...cur, ...data } : cur
                );
                toast.success(`Marked as ${STATUS_LABEL[status]}`);
                // refresh stats
                axios
                    .get(`${API}/admin/bookings/stats`, { headers: authHeaders })
                    .then((r) => setStats(r.data))
                    .catch(() => {});
            } catch {
                toast.error("Could not update status");
            }
        },
        [authHeaders]
    );

    const doDelete = useCallback(
        async (id) => {
            try {
                await axios.delete(`${API}/admin/bookings/${id}`, {
                    headers: authHeaders,
                });
                setBookings((prev) => prev.filter((b) => b.id !== id));
                setSelected((cur) => (cur && cur.id === id ? null : cur));
                toast.success("Booking deleted");
                load();
            } catch {
                toast.error("Could not delete booking");
            } finally {
                setConfirmDelete(null);
            }
        },
        [authHeaders, load]
    );

    const statCards = useMemo(
        () => [
            { key: "total", label: "Total", value: stats.total || 0 },
            ...STATUS_FLOW.map((s) => ({
                key: s,
                label: STATUS_LABEL[s],
                value: stats.by_status?.[s] || 0,
            })),
        ],
        [stats]
    );

    return (
        <div
            data-testid="admin-dashboard"
            className="min-h-screen bg-[#fdfdfd] text-[#0a0a0a]"
        >
            {/* Top Bar */}
            <header className="sticky top-0 z-30 bg-[#fdfdfd] border-b hairline">
                <div className="mx-auto max-w-[1400px] flex items-center justify-between px-6 py-4">
                    <Link to="/" className="flex items-center gap-3">
                        <img
                            src={BRAND.logo}
                            alt={BRAND.legal}
                            className="h-9 w-9 object-contain mix-blend-multiply"
                        />
                        <div className="leading-tight">
                            <div className="font-display text-[14px] tracking-tight">
                                QUISEO ·{" "}
                                <span className="text-[#1d4ed8]">DISPATCH</span>
                            </div>
                            <div className="font-mono-tech text-[9px] text-[#52525b] mt-0.5">
                                ADMIN CONSOLE
                            </div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-3">
                        <button
                            data-testid="refresh-button"
                            onClick={load}
                            className="font-mono-tech text-[11px] inline-flex items-center gap-2 border border-[#0a0a0a]/15 px-3 py-2 hover:bg-[#0a0a0a] hover:text-white transition-colors"
                        >
                            <RefreshCw
                                className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
                            />
                            Refresh
                        </button>
                        <span className="hidden sm:inline font-mono-tech text-[10px] text-[#52525b]">
                            {user?.email}
                        </span>
                        <button
                            data-testid="admin-logout"
                            onClick={logout}
                            className="btn-ghost font-mono-tech text-[11px] px-3 py-2 inline-flex items-center gap-2"
                        >
                            <LogOut className="w-3.5 h-3.5" /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-[1400px]">
                {/* Tab strip */}
                <section className="grid grid-cols-2 border-b hairline">
                    <button
                        data-testid="tab-bookings"
                        onClick={() => setTab("bookings")}
                        className={`px-5 py-4 font-mono-tech text-[11px] tracking-[0.18em] uppercase text-left border-r hairline transition-colors ${
                            tab === "bookings"
                                ? "bg-[#0a0a0a] text-white"
                                : "bg-white text-[#0a0a0a] hover:bg-[#f4f4f5]"
                        }`}
                    >
                        Bookings · {bookings.length}
                    </button>
                    <button
                        data-testid="tab-messages"
                        onClick={() => setTab("messages")}
                        className={`px-5 py-4 font-mono-tech text-[11px] tracking-[0.18em] uppercase text-left transition-colors ${
                            tab === "messages"
                                ? "bg-[#0a0a0a] text-white"
                                : "bg-white text-[#0a0a0a] hover:bg-[#f4f4f5]"
                        }`}
                    >
                        Messages · {messages.length}
                        {messages.length > 0 && tab !== "messages" && (
                            <span className="ml-2 inline-block w-2 h-2 bg-[#1d4ed8] rounded-full" />
                        )}
                    </button>
                </section>

                {tab === "bookings" && (
                  <div>
                {/* Stats */}
                <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border-l hairline">
                    {statCards.map((s) => (
                        <button
                            key={s.key}
                            data-testid={`stat-${s.key}`}
                            onClick={() =>
                                setFilter(s.key === "total" ? "all" : s.key)
                            }
                            className={`border-r border-b hairline p-5 text-left transition-colors ${
                                filter === (s.key === "total" ? "all" : s.key)
                                    ? "bg-[#0a0a0a] text-white"
                                    : "bg-white hover:bg-[#f4f4f5]"
                            }`}
                        >
                            <div className="font-mono-tech text-[10px] opacity-70">
                                {s.label}
                            </div>
                            <div className="font-display text-3xl mt-2">
                                {s.value}
                            </div>
                        </button>
                    ))}
                </section>

                {/* Toolbar */}
                <div className="flex items-center justify-between px-6 py-5 border-b hairline">
                    <div className="font-mono-tech text-[11px] text-[#52525b]">
                        {bookings.length} {bookings.length === 1 ? "booking" : "bookings"}
                        {filter !== "all" ? ` · ${STATUS_LABEL[filter]}` : ""}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="font-mono-tech text-[10px] text-[#52525b]">
                            Filter
                        </span>
                        <Select value={filter} onValueChange={setFilter}>
                            <SelectTrigger
                                data-testid="filter-select"
                                className="border border-[#0a0a0a]/15 px-3 py-2 h-auto font-mono-tech text-[11px] min-w-[140px]"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="border border-[#0a0a0a] rounded-none">
                                <SelectItem value="all" className="rounded-none">
                                    All
                                </SelectItem>
                                {STATUS_FLOW.map((s) => (
                                    <SelectItem
                                        key={s}
                                        value={s}
                                        className="rounded-none"
                                    >
                                        {STATUS_LABEL[s]}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Bookings Table */}
                <div className="overflow-x-auto">
                    <table className="w-full" data-testid="bookings-table">
                        <thead className="bg-[#0a0a0a] text-white">
                            <tr className="font-mono-tech text-[10px] tracking-[0.18em]">
                                <th className="text-left px-5 py-3 font-medium">
                                    Customer
                                </th>
                                <th className="text-left px-5 py-3 font-medium">
                                    Service
                                </th>
                                <th className="text-left px-5 py-3 font-medium hidden md:table-cell">
                                    Vehicle
                                </th>
                                <th className="text-left px-5 py-3 font-medium hidden lg:table-cell">
                                    When
                                </th>
                                <th className="text-left px-5 py-3 font-medium">
                                    Status
                                </th>
                                <th className="px-5 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {bookings.length === 0 && !loading && (
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="text-center py-16 font-mono-tech text-[11px] text-[#52525b]"
                                    >
                                        No bookings yet
                                    </td>
                                </tr>
                            )}
                            {bookings.map((b) => (
                                <tr
                                    key={b.id}
                                    data-testid={`booking-row-${b.id}`}
                                    onClick={() => setSelected(b)}
                                    className="border-b hairline hover:bg-[#f4f4f5] cursor-pointer transition-colors"
                                >
                                    <td className="px-5 py-4 align-top">
                                        <div className="font-display text-[15px] leading-tight">
                                            {b.full_name}
                                        </div>
                                        <div className="font-mono-tech text-[10px] text-[#52525b] mt-1">
                                            {b.phone}
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 align-top">
                                        <div className="text-[14px]">
                                            {b.service_type}
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 align-top hidden md:table-cell text-[13px]">
                                        {b.vehicle_year} {b.vehicle_make}{" "}
                                        {b.vehicle_model}
                                    </td>
                                    <td className="px-5 py-4 align-top hidden lg:table-cell font-mono-tech text-[11px] text-[#52525b]">
                                        {b.preferred_date}
                                        <br />
                                        {b.preferred_time}
                                    </td>
                                    <td className="px-5 py-4 align-top">
                                        <StatusBadge status={b.status} />
                                    </td>
                                    <td className="px-5 py-4 align-top text-right">
                                        <ChevronRight className="w-4 h-4 inline opacity-40" />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                  </div>
                )}

                {tab === "messages" && (
                  <div data-testid="messages-tab">
                    <div className="px-6 py-4 border-b hairline bg-[#f4f4f5]">
                      <div className="font-mono-tech text-[10px] text-[#52525b]">
                        These are submissions from the &ldquo;Contact&rdquo; section at the bottom of your site.
                        Some customers use this instead of the booking form — always check both tabs.
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="messages-table">
                        <thead className="bg-[#0a0a0a] text-white">
                          <tr className="font-mono-tech text-[10px] tracking-[0.18em]">
                            <th className="text-left px-5 py-3 font-medium">From</th>
                            <th className="text-left px-5 py-3 font-medium">Message</th>
                            <th className="text-left px-5 py-3 font-medium hidden md:table-cell">When</th>
                          </tr>
                        </thead>
                        <tbody>
                          {messages.length === 0 && !loading && (
                            <tr>
                              <td colSpan={3} className="text-center py-16 font-mono-tech text-[11px] text-[#52525b]">
                                No messages yet
                              </td>
                            </tr>
                          )}
                          {messages.map((m) => (
                            <tr key={m.id} data-testid={`message-row-${m.id}`} className="border-b hairline">
                              <td className="px-5 py-4 align-top min-w-[180px]">
                                <div className="font-display text-[15px] leading-tight">{m.name}</div>
                                <a href={`mailto:${m.email}`} className="font-mono-tech text-[10px] text-[#1d4ed8] hover:underline block mt-1 break-all">
                                  {m.email}
                                </a>
                                {m.phone ? (
                                  <a href={`tel:${m.phone}`} className="font-mono-tech text-[10px] text-[#1d4ed8] hover:underline block mt-1">
                                    {m.phone}
                                  </a>
                                ) : null}
                              </td>
                              <td className="px-5 py-4 align-top text-[14px] whitespace-pre-wrap">
                                {m.message}
                              </td>
                              <td className="px-5 py-4 align-top hidden md:table-cell font-mono-tech text-[10px] text-[#52525b]">
                                {fmtDate(m.created_at)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
            </main>

            {/* Detail Drawer */}
            {selected && (
                <div
                    className="fixed inset-0 z-40 bg-[#0a0a0a]/60 backdrop-blur-sm"
                    onClick={() => setSelected(null)}
                >
                    <aside
                        data-testid="booking-detail-drawer"
                        onClick={(e) => e.stopPropagation()}
                        className="absolute top-0 right-0 h-full w-full sm:w-[480px] bg-[#fdfdfd] border-l hairline overflow-y-auto"
                    >
                        <div className="sticky top-0 bg-[#0a0a0a] text-white px-6 py-4 flex items-center justify-between">
                            <div>
                                <div className="font-mono-tech text-[10px] text-white/60">
                                    BOOKING / {selected.id.slice(0, 8)}
                                </div>
                                <div className="font-display text-lg mt-1">
                                    {selected.full_name}
                                </div>
                            </div>
                            <button
                                data-testid="close-drawer"
                                onClick={() => setSelected(null)}
                                className="text-white/70 hover:text-white"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div>
                                <div className="font-mono-tech text-[10px] text-[#52525b] mb-2">
                                    STATUS
                                </div>
                                <div className="flex items-center gap-3 mb-3">
                                    <StatusBadge status={selected.status} />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    {STATUS_FLOW.map((s) => (
                                        <button
                                            key={s}
                                            data-testid={`set-status-${s}`}
                                            onClick={() =>
                                                updateStatus(selected.id, s)
                                            }
                                            disabled={selected.status === s}
                                            className={`font-mono-tech text-[10px] px-3 py-2.5 border border-[#0a0a0a]/15 transition-colors ${
                                                selected.status === s
                                                    ? "bg-[#0a0a0a] text-white border-[#0a0a0a] cursor-not-allowed"
                                                    : "bg-white hover:bg-[#0a0a0a] hover:text-white"
                                            }`}
                                        >
                                            {STATUS_LABEL[s]}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="border-t hairline pt-6 space-y-4">
                                <Row icon={Phone} label="Phone">
                                    <a
                                        href={`tel:${selected.phone}`}
                                        className="text-[#1d4ed8] hover:underline"
                                    >
                                        {selected.phone}
                                    </a>
                                </Row>
                                <Row icon={Mail} label="Email">
                                    <a
                                        href={`mailto:${selected.email}`}
                                        className="text-[#1d4ed8] hover:underline break-all"
                                    >
                                        {selected.email}
                                    </a>
                                </Row>
                                <Row icon={Wrench} label="Service">
                                    {selected.service_type}
                                </Row>
                                <Row icon={Car} label="Vehicle">
                                    {selected.vehicle_year}{" "}
                                    {selected.vehicle_make}{" "}
                                    {selected.vehicle_model}
                                </Row>
                                <Row icon={CalendarIcon} label="When">
                                    {selected.preferred_date} ·{" "}
                                    {selected.preferred_time}
                                </Row>
                                <Row icon={MapPin} label="Address">
                                    {selected.address}
                                </Row>
                                {selected.issue && (
                                    <Row label="Issue">
                                        <span className="whitespace-pre-wrap">
                                            {selected.issue}
                                        </span>
                                    </Row>
                                )}
                                <Row label="Submitted">
                                    <span className="font-mono-tech text-[11px]">
                                        {fmtDate(selected.created_at)}
                                    </span>
                                </Row>
                            </div>

                            <div className="border-t hairline pt-6 flex items-center gap-3">
                                <a
                                    href={`tel:${selected.phone}`}
                                    className="btn-primary px-4 py-3 font-mono-tech text-[11px] inline-flex items-center gap-2 flex-1 justify-center"
                                >
                                    <Phone className="w-3.5 h-3.5" />
                                    Call
                                </a>
                                <button
                                    data-testid="delete-booking"
                                    onClick={() => setConfirmDelete(selected)}
                                    className="px-4 py-3 border border-[#0a0a0a]/15 font-mono-tech text-[11px] inline-flex items-center gap-2 hover:bg-[#0a0a0a] hover:text-white transition-colors"
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Delete
                                </button>
                            </div>
                        </div>
                    </aside>
                </div>
            )}

            <AlertDialog
                open={!!confirmDelete}
                onOpenChange={(o) => !o && setConfirmDelete(null)}
            >
                <AlertDialogContent className="rounded-none border border-[#0a0a0a]">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display text-2xl">
                            Delete this booking?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This permanently removes {confirmDelete?.full_name}
                            &rsquo;s booking. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none border border-[#0a0a0a]/15 font-mono-tech text-[11px]">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="confirm-delete"
                            onClick={() => doDelete(confirmDelete.id)}
                            className="rounded-none bg-[#0a0a0a] text-white font-mono-tech text-[11px] hover:bg-[#1d4ed8]"
                        >
                            Delete booking
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function Row({ icon: Icon, label, children }) {
    return (
        <div className="flex items-start gap-3">
            {Icon ? (
                <Icon
                    className="w-4 h-4 text-[#1d4ed8] mt-0.5 shrink-0"
                    strokeWidth={1.5}
                />
            ) : (
                <span className="w-4 inline-block" />
            )}
            <div className="flex-1">
                <div className="font-mono-tech text-[10px] text-[#52525b] mb-1">
                    {label}
                </div>
                <div className="text-[14px] leading-snug">{children}</div>
            </div>
        </div>
    );
}
